// src/pages/Payments.js
import React, { useEffect, useState, useCallback } from "react";
import {
    Card,
    Table,
    InputNumber,
    Input,
    Tooltip,
    Button,
    Upload,
    message,
    Row,
    Col,
    Select,
    Modal,
} from "antd";
import api from "../api/axios";
import moment from "moment";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";

// for sample csv download
const sampleCsvContent = `InvoiceID,Payment,TDS,PaymentDate,Paid_Via,UTR_Ref,Remarks
101,5000,0,2025-12-01,NEFT,UTR123456,"First part payment"
102,12000,500,2025-12-02,IMPS,UTR987654,"Full payment with TDS"
103,7500,0,2025-12-03,Cheque,CHQ448822,"Advance payment"
`;

function downloadSampleCsv() {
    const blob = new Blob([sampleCsvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "purchase-payments-sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

const { Option } = Select;

export default function Payments() {
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [editFields, setEditFields] = useState({});
    const [loading, setLoading] = useState(false);

    // Filter states
    const [dateFilter, setDateFilter] = useState("");
    const [supplierFilter, setSupplierFilter] = useState([]);
    const [poNumberFilter, setPONumberFilter] = useState([]);
    const [statusFilter, setStatusFilter] = useState([]);

    // Payout module states
    const [exportingPayout, setExportingPayout] = useState(false);
    const [importingPayout, setImportingPayout] = useState(false);
    const [payoutModalOpen, setPayoutModalOpen] = useState(false);
    const [payoutImportReport, setPayoutImportReport] = useState([]);
    const [payoutImportSummary, setPayoutImportSummary] = useState({
        success: 0,
        failed: 0,
        logFile: "",
    });

    const fetchInvoices = useCallback(() => {
        setLoading(true);
        api
            .get("/payments/purchase")
            .then((res) => {
                setInvoices(res.data);
                setFilteredInvoices(res.data);

                const fields = {};
                res.data.forEach((inv) => {
                    const pending = Math.max(
                        0,
                        (Number(inv.TotalAmount) || 0) -
                        (Number(inv.AmountPaid) || 0) -
                        (Number(inv.TDS) || 0)
                    );

                    fields[inv.InvoiceID] = {
                        Payment: pending,
                        TDS:
                            inv.TDS !== undefined
                                ? Number(inv.TDS) || 0
                                : Math.round((Number(inv.TaxAmount) || 0) * 0.1),
                        PaymentDate: "",
                        Paid_Via: inv.Paid_Via || "",
                        UTR_Ref: inv.UTR_Ref || "",
                        Remarks: inv.Remarks || "",
                    };
                });

                setEditFields(fields);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    const handleExportPayoutModule = async () => {
        try {
            const exportRows = filteredInvoices
                .map((inv) => {
                    const ef = editFields[inv.InvoiceID] || {};

                    const payment =
                        Number(
                            ef.Payment ??
                            Math.max(
                                0,
                                (Number(inv.TotalAmount) || 0) -
                                (Number(inv.AmountPaid) || 0) -
                                (Number(inv.TDS) || 0)
                            )
                        ) || 0;

                    const tds =
                        Number(
                            ef.TDS !== undefined
                                ? ef.TDS
                                : inv.TDS !== undefined
                                    ? inv.TDS
                                    : 0
                        ) || 0;

                    return {
                        InvoiceID: inv.InvoiceID,
                        Payment: payment,
                        TDS: tds,
                        PaymentDate: (ef.PaymentDate || "").trim(),
                        Paid_Via: ef.Paid_Via || "",
                        UTR_Ref: ef.UTR_Ref || "",
                        Remarks: ef.Remarks || "",
                    };
                })
                .filter((row) => row.PaymentDate);

            if (!exportRows.length) {
                message.warning("Please enter Payment Date for at least one record to export");
                return;
            }

            setExportingPayout(true);

            const response = await api.post(
                "/payments/purchase/payout-module/export",
                {
                    supplierFilter,
                    poNumberFilter,
                    statusFilter,
                    dateFilter,
                    exportRows,
                },
                {
                    responseType: "blob",
                }
            );

            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Payout_Module.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            message.success("Payout module exported");
        } catch (err) {
            console.error("Export payout module error:", err);
            message.error("Failed to export payout module");
        } finally {
            setExportingPayout(false);
        }
    };

    const handleImportPayoutModule = async ({ file, onSuccess, onError }) => {
        const formData = new FormData();
        formData.append("file", file);

        try {
            setImportingPayout(true);

            const response = await api.post(
                "/payments/purchase/payout-module/import",
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            setPayoutImportReport(response.data.report || []);
            setPayoutImportSummary({
                success: response.data.success || 0,
                failed: response.data.failed || 0,
                logFile: response.data.logFile || "",
            });
            setPayoutModalOpen(true);

            message.success("Payout module imported");
            fetchInvoices();

            if (onSuccess) onSuccess(response.data);
        } catch (err) {
            console.error("Import payout module error:", err);
            message.error(err?.response?.data?.error || "Failed to import payout module");
            if (onError) onError(err);
        } finally {
            setImportingPayout(false);
        }
    };

    const payoutModuleUploadProps = {
        accept: ".xlsx",
        showUploadList: false,
        customRequest: handleImportPayoutModule,
    };

    const getPendingAmount = useCallback(
        (record) => {
            return Math.max(
                0,
                (Number(record.TotalAmount) || 0) -
                (Number(record.AmountPaid) || 0) -
                (Number(record.TDS) || 0)
            );
        },
        []
    );

    const getStatus = useCallback(
        (record) => {
            const pending = getPendingAmount(record);
            if ((Number(record.AmountPaid) || 0) === 0) return "Pending";
            if (pending === 0) return "Full";
            return "Partial";
        },
        [getPendingAmount]
    );

    useEffect(() => {
        let data = [...invoices];

        if (supplierFilter.length) {
            data = data.filter((inv) => supplierFilter.includes(inv.SupplierName));
        }

        if (poNumberFilter.length) {
            data = data.filter((inv) => poNumberFilter.includes(inv.PO_Number));
        }

        if (statusFilter.length) {
            data = data.filter((inv) => statusFilter.includes(getStatus(inv)));
        } else {
            data = data.filter((inv) => getStatus(inv) !== "Full");
        }

        if (dateFilter.trim()) {
            const m = dateFilter.trim().match(/^([<>]=?|=)\s*(\d{4}-\d{2}-\d{2})$/);

            if (m) {
                const op = m[1];
                const filterDate = m[2];

                data = data.filter((inv) => {
                    if (!inv.Date) return false;

                    const invDate = moment(inv.Date, ["YYYY-MM-DD", moment.ISO_8601], true);
                    if (!invDate.isValid()) return false;

                    if (op === ">") return invDate.isAfter(filterDate, "day");
                    if (op === "<") return invDate.isBefore(filterDate, "day");
                    if (op === ">=") return invDate.isSameOrAfter(filterDate, "day");
                    if (op === "<=") return invDate.isSameOrBefore(filterDate, "day");
                    if (op === "=") return invDate.isSame(filterDate, "day");

                    return true;
                });
            }
        }

        setFilteredInvoices(data);
    }, [
        supplierFilter,
        poNumberFilter,
        dateFilter,
        statusFilter,
        invoices,
        getStatus,
    ]);

    const handleFieldChange = (id, field, value) => {
        setEditFields((prev) => {
            const prevRow = prev[id] || {};
            let newRow = { ...prevRow, [field]: value };

            if (field === "TDS") {
                const invoice = invoices.find((inv) => inv.InvoiceID === id);
                if (invoice) {
                    const maxTDS = Math.round((Number(invoice.RateAmount) || 0) * 0.1);
                    const validTDS = Math.min(Number(value) || 0, maxTDS);
                    const pending = Math.max(
                        0,
                        (Number(invoice.TotalAmount) || 0) - (Number(invoice.AmountPaid) || 0)
                    );

                    newRow.TDS = validTDS;
                    newRow.Payment = Math.max(0, pending - validTDS);
                }
            }

            return { ...prev, [id]: newRow };
        });
    };

    const bulkPaymentUploadProps = {
        name: "file",
        action: "/payments/purchase/bulk",
        accept: ".csv",
        showUploadList: false,
        headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        onChange(info) {
            if (info.file.status === "done") {
                message.success("Bulk payments processed");
                console.log("Report:", info.file.response);
                fetchInvoices();
            } else if (info.file.status === "error") {
                message.error("Bulk payments failed");
                console.error("Bulk upload error:", info.file.response);
            }
        },
    };

    // ... keep the rest of your existing columns, handlers, and return JSX below this




    // handle make payment for each record
  const handleMakePayment = (record) => {
    const fields = editFields[record.InvoiceID] || {};
    const Payment = Number(fields.Payment) || 0;
    const currentTDS = Number(fields.TDS) || 0;
    const storedTDS = Number(record.TDS) || 0;
    const defaultTDS = Math.round((record.RateAmount || 0) * 0.1);

    // Only send TDS if the stored TDS is NOT already the default
    const sendTDS = (storedTDS !== defaultTDS) ? currentTDS : 0;
    const pendingAmount = Math.max(0, (record.TotalAmount || 0) - (record.AmountPaid || 0));

    // Show warning confirmation dialog only on the first attempt
    if ((Payment + sendTDS ) >= pendingAmount  && sendTDS < defaultTDS) {
        if (!window.confirm("Warning: Payment >= pending amount but TDS is not deducted or less than 10%. Do you want to proceed?")) {
          return; // User clicked 'Cancel', do not proceed
        }
    }

    api.put(`/payments/purchase/${record.InvoiceID}/payment`, {
      Payment,
      TDS: sendTDS,
      PaymentDate: fields.PaymentDate || new Date().toISOString().slice(0, 10),
      Paid_Via: fields.Paid_Via || "",
      UTR_Ref: fields.UTR_Ref || "",
      Remarks: fields.Remarks || "",
        })
        .then((res) => {
          message.success("Payment updated!");
          fetchInvoices();
          setEditFields((prev) => ({ ...prev, [record.InvoiceID]: {} }));
        })
        .catch((e) => {
          message.error(e.response?.data?.error || "Payment failed");
        });
  };

  // Styles
  const numStyle = { width: 60, fontSize: 13, padding: "0 4px" };
  const numEditStyle = { width: 90, fontSize: 13, padding: "0 4px" };
  const cellStyle = { width: 110, fontSize: 13, padding: "0 4px" };
  const headerStyle = { fontSize: 13, fontWeight: "bold", padding: 0 };

  // Filter options
  const supplierOptions = Array.from(new Set(invoices.map((inv) => inv.SupplierName)))
      .filter(Boolean)
      .map((name) => ({ text: name, value: name }));

  const poNumberOptions = Array.from(new Set(invoices.map((inv) => inv.PO_Number)))
      .filter(Boolean)
      .map((po) => ({ text: po, value: po }));

  const statusOptions = [
    { text: "Pending", value: "Pending" },
    { text: "Partial", value: "Partial" },
    { text: "Full", value: "Full" },
  ];

  const columns = [
    {
     title: 'Invoice ID',
     dataIndex: 'InvoiceID',
     key: 'InvoiceID',
     width: 100,
    },
    {
      title: <span style={headerStyle}>Invoice#</span>,
      dataIndex: "InvoiceNumber",
      width: 80,
      sorter: (a, b) => (a.InvoiceNumber || "").localeCompare(b.InvoiceNumber || ""),
      render: (v) => <span style={cellStyle}>{v}</span>,
    },
    {
      title: <span style={headerStyle}>Supplier</span>,
      dataIndex: "SupplierName",
      sorter: (a, b) => (a.SupplierName || "").localeCompare(b.SupplierName || ""),
      render: (v) => <span style={cellStyle}>{v}</span>,
    },
    {
      title: <span style={headerStyle}>Date</span>,
      dataIndex: "Date",
      width: 30,
      sorter: (a, b) => new Date(a.Date) - new Date(b.Date),
      render: (v) => <span style={cellStyle}>{v}</span>,
    },
    {
      title: <span style={headerStyle}>PO#</span>,
      dataIndex: "PO_Number",
      width: 60,
      sorter: (a, b) => (a.PO_Number || "").localeCompare(b.PO_Number || ""),
      render: (v) => <span style={cellStyle}>{v}</span>,
    },
    {
      title: <span style={headerStyle}>Rate</span>,
      dataIndex: "RateAmount",
      width: 30,
      sorter: (a, b) => a.RateAmount - b.RateAmount,
      render: (v) => <span style={numStyle}>{v}</span>,
    },
    {
      title: <span style={headerStyle}>Tax,GST</span>,
      dataIndex: "TaxAmount",
      sorter: (a, b) => a.TaxAmount - b.TaxAmount,
      render: (v) => <span style={numStyle}>{v}</span>,
    },
    {
      title: <span style={headerStyle}>Total</span>,
      dataIndex: "TotalAmount",
      sorter: (a, b) => a.TotalAmount - b.TotalAmount,
      render: (v) => <span style={numStyle}>{v}</span>,
    },
    {
      title: <span style={headerStyle}>Pending</span>,
      render: (_, record) => <span style={numStyle}>{getPendingAmount(record)}</span>,
      sorter: (a, b) => getPendingAmount(a) - getPendingAmount(b),
    },
    {
      title: <span style={headerStyle}>Status</span>,
      sorter: (a, b) => getStatus(a).localeCompare(getStatus(b)),
      render: (_, record) => {
        const status = getStatus(record);
        return (
            <span
                style={{
                  color: status !== "Full" ? "red" : "green",
                  ...numStyle,
                }}
            >
            {status}
          </span>
        );
      },
    },
    {
      title: <span style={headerStyle}>TDS Deducted</span>,
      render: (_, record) => {
        if (getStatus(record) === "Full") return null;
        const maxTDS = Math.round((record.RateAmount || 0) * 0.1);
        const currentTDS =
            editFields[record.InvoiceID]?.TDS !== undefined
                ? editFields[record.InvoiceID]?.TDS
                : Number(record.TDS) || 0;
        const isLocked = currentTDS === maxTDS;
        return (
            <Tooltip title={isLocked ? "TDS is 10% of RateAmount and locked" : `Enter TDS (max ${maxTDS})`}>
              <InputNumber
                  min={0}
                  max={maxTDS}
                  value={currentTDS}
                  onChange={val => handleFieldChange(record.InvoiceID, "TDS", val)}
                  style={{
                    ...numEditStyle,
                    backgroundColor: isLocked ? "#f5f5f5" : undefined,
                    color: isLocked ? "#999" : undefined
                  }}
                  disabled={isLocked}
              />
            </Tooltip>
        );
      },
      sorter: (a, b) => (Number(a.TDS) || 0) - (Number(b.TDS) || 0),
    },

    {
      title: <span style={headerStyle}>Payment</span>,
      render: (_, record) =>
          getStatus(record) === "Full" ? null : (
              <InputNumber
                  min={0}
                  disabled={getPendingAmount(record) === 0}
                  value={editFields[record.InvoiceID]?.Payment}
                  onChange={val => handleFieldChange(record.InvoiceID, "Payment", val)}
                  style={numEditStyle}
              />
          ),
      sorter: (a, b) => {
        const aVal = editFields[a.InvoiceID]?.Payment || 0;
        const bVal = editFields[b.InvoiceID]?.Payment || 0;
        return aVal - bVal;
      },
    },
    {
      title: <span style={headerStyle}>Payment Date</span>,
      render: (_, record) =>
          getStatus(record) === "Full" ? null : (
              <Input
                  type="date"
                  value={editFields[record.InvoiceID]?.PaymentDate}
                  onChange={e => handleFieldChange(record.InvoiceID, "PaymentDate", e.target.value)}
                  style={{ ...numEditStyle, minWidth: 90 }}
              />
          ),
      sorter: (a, b) => {
        const aVal = editFields[a.InvoiceID]?.PaymentDate || "";
        const bVal = editFields[b.InvoiceID]?.PaymentDate || "";
        return (aVal || "").localeCompare(bVal || "");
      },
    },
      {
          title: "Paid Via",
          render: (_, record) =>
              getStatus(record) === "Full" ? null : (
                  <Input
                      value={editFields[record.InvoiceID]?.Paid_Via}
                      onChange={e =>
                          handleFieldChange(record.InvoiceID, "Paid_Via", e.target.value)
                      }
                      style={{ ...numEditStyle, minWidth: 90 }}
                  />
              ),
          sorter: (a, b) => {
              const aVal = editFields[a.InvoiceID]?.Paid_Via || "";
              const bVal = editFields[b.InvoiceID]?.Paid_Via || "";
              return (aVal || "").localeCompare(bVal || "");
          }
      },
      {
          title: "UTR/Ref",
          render: (_, record) =>
              getStatus(record) === "Full" ? null : (
                  <Input
                      value={editFields[record.InvoiceID]?.UTR_Ref}
                      onChange={e =>
                          handleFieldChange(record.InvoiceID, "UTR_Ref", e.target.value)
                      }
                      style={{ ...numEditStyle, minWidth: 90 }}
                  />
              ),
          sorter: (a, b) => {
              const aVal = editFields[a.InvoiceID]?.UTR_Ref || "";
              const bVal = editFields[b.InvoiceID]?.UTR_Ref || "";
              return (aVal || "").localeCompare(bVal || "");
          }
      },
    {
      title: <span style={headerStyle}>Remarks</span>,
      render: (_, record) =>
          getStatus(record) === "Full"
              ? null
              : (
                  <Input.TextArea
                      rows={1}
                      value={editFields[record.InvoiceID]?.Remarks}
                      onChange={e => handleFieldChange(record.InvoiceID, "Remarks", e.target.value)}
                      style={{ ...numEditStyle, height: 28, minWidth: 60, maxWidth: 110 }}
                      readOnly={getStatus(record) === "Full"}
                      disabled={getStatus(record) === "Full"}
                  />
              ),
    },
    {
      title: <span style={headerStyle}>Action</span>,
      render: (_, record) =>
          getStatus(record) === "Full"
              ? null
              : (
                  <Button
                      type="primary"
                      disabled={getPendingAmount(record) === 0}
                      onClick={() => handleMakePayment(record)}
                      style={{ ...numEditStyle, height: 28 }}
                  >
                    Make Payment
                  </Button>
              ),
    },

  ];

  return (
    <>
      <Card title="Purchase Payments" style={{ width: "100%", maxWidth: "100%" }}>
        {/* Filter Controls */}
        <Row gutter={8} style={{ marginBottom: 16 }}>
          <Col>
            <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Filter by Supplier"
                style={{ width: 180 }}
                onChange={setSupplierFilter}
                value={supplierFilter}
                filterOption={(input, option) =>
                    option.children && option.children.toLowerCase().startsWith(input.toLowerCase())
                }
            >
              {supplierOptions.map((opt) => (
                  <Option value={opt.value} key={opt.value}>{opt.text}</Option>
              ))}
            </Select>

          </Col>
          <Col>
            <Tooltip title="Type: >2025-01-21, <2025-05-11, >=2025-12-17, =2025-01-15">
              <Input
                  placeholder="Date (e.g. >2025-01-31)"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  style={{ width: 170 }}
              />
            </Tooltip>
          </Col>
          <Col>
            <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Filter by PO Number"
                style={{ width: 140 }}
                onChange={setPONumberFilter}
                value={poNumberFilter}
                filterOption={(input, option) =>
                    option.children && option.children.toLowerCase().startsWith(input.toLowerCase())
                }
            >
              {poNumberOptions.map((opt) => (
                  <Option value={opt.value} key={opt.value}>{opt.text}</Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
                mode="multiple"
                allowClear
                placeholder="Filter by Status"
                style={{ width: 120 }}
                onChange={setStatusFilter}
                value={statusFilter}
            >
              {statusOptions.map((opt) => (
                  <Option value={opt.value} key={opt.value}>{opt.text}</Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Button
                onClick={() => {
                  setSupplierFilter([]);
                  setPONumberFilter([]);
                  setDateFilter("");
                  setStatusFilter([]);
                }}
            >
              Reset
            </Button>
          </Col>
          <Col>
                <Upload {...bulkPaymentUploadProps}>
                    <Button type="primary">
                        Bulk Payment (CSV)
                    </Button>
                </Upload>
          </Col>
          <Col>
                <Button icon={<DownloadOutlined />} onClick={downloadSampleCsv}>
                    Sample CSV
                </Button>
          </Col>
          <Col>
                <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExportPayoutModule}
                    loading={exportingPayout}
                >
                    Export Payout Module
                </Button>
          </Col>
          <Col>
                <Upload {...payoutModuleUploadProps}>
                    <Button icon={<UploadOutlined />} loading={importingPayout}>
                        Upload Payout Module
                    </Button>
                </Upload>
          </Col>
        </Row>
        <Table
            dataSource={filteredInvoices}
            columns={columns}
            rowKey="InvoiceID"
            pagination={{ pageSize: 20 }}
            scroll={{ x: "max-content" }}
            loading={loading}
        />
      </Card>
        <Modal
            title="Payout Import Results"
            open={payoutModalOpen}
            onCancel={() => setPayoutModalOpen(false)}
            footer={[
                <Button key="close" onClick={() => setPayoutModalOpen(false)}>
                    Close
                </Button>,
            ]}
            width={1000}
        >
            <div style={{ marginBottom: 12 }}>
                <b>Success:</b> {payoutImportSummary.success}
                <span style={{ marginLeft: 16 }}>
      <b>Failed:</b> {payoutImportSummary.failed}
    </span>
                {payoutImportSummary.logFile && (
                    <span style={{ marginLeft: 16 }}>
        <a
            href={payoutImportSummary.logFile}
            target="_blank"
            rel="noopener noreferrer"
        >
          Download Log CSV
        </a>
      </span>
                )}
            </div>

            <Table
                rowKey={(row, idx) => idx}
                size="small"
                pagination={{ pageSize: 10 }}
                scroll={{ x: "max-content" }}
                dataSource={payoutImportReport}
                columns={[
                    { title: "InvoiceID", dataIndex: "invoiceId" },
                    { title: "Invoice#", dataIndex: "invoiceNumber" },
                    { title: "Supplier", dataIndex: "supplier" },
                    { title: "Result", dataIndex: "status" },
                    { title: "Amount Paid", dataIndex: "AmountPaid" },
                    { title: "Status", dataIndex: "Status" },
                    { title: "Pending", dataIndex: "PendingAmount" },
                    { title: "Error", dataIndex: "error" },
                ]}
            />
        </Modal>
      </>

  );


}
